import { useBucketsQuery } from "@/pages/sy/hooks/queries/useBucketsQuery";
import { ErrorModal } from "@dexteel/mesf-core";
import { useEffect, useState } from "react";
import { Box, CircularProgress, TextField } from "@mui/material";
import { Autocomplete } from "@mui/material";
import type { FieldError } from "react-hook-form";
import type { Bucket } from "../model/Bucket";

type Props = {
  label: string;
  value: number | null;
  onChange?: (val: number | null) => void;
  onBucketSelected?: (bucket: Bucket | null) => void;
  fieldError?: FieldError | undefined;
  disabled?: boolean;
  defaultSelection?: string;
  isManualCharge?: boolean;
};

export const BucketAutocomplete = ({
  label,
  value,
  onChange = () => {},
  onBucketSelected = () => {},
  fieldError,
  disabled = false,
  defaultSelection,
  isManualCharge = false,
}: Props) => {
  const [error, setError] = useState("");
  const { data: buckets = [], isLoading } = useBucketsQuery((err) =>
    setError(err.message),
  );

  const normalize = (str: string) => str.replace(/\s+/g, "");

  const selectedValue =
    buckets.find((bucket) => bucket.BucketId === value) || null;

  useEffect(() => {
    if (defaultSelection && value == null && buckets.length > 0) {
      const match = buckets.find(
        (b) => normalize(b.BucketName) === normalize(defaultSelection),
      );
      if (match) {
        onChange(match.BucketId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buckets, defaultSelection]);

  return (
    <>
      <Autocomplete
        id="clear-on-escape"
        clearOnEscape
        options={buckets ?? []}
        getOptionLabel={(option) => {
          if (isManualCharge && option.BucketName.trim() !== "Unknown") {
            const status: "Closed" | "" = option.IsClosed ? "Closed" : "";
            const weight: string = option.BucketChargedWeight
              ? ` (${option.BucketChargedWeight} lbs)`
              : "";
            return `${option.BucketName} ${status}${weight}`;
          } else {
            return option.BucketName;
          }
        }}
        renderOption={(props, option) => (
          <li {...props}>
            <span>{option.BucketName} </span>
            {isManualCharge && option.BucketName.trim() !== "Unknown" && (
              <>
                <Box
                  component="span"
                  sx={{ color: option.IsClosed ? "red" : "inherit" }}
                >
                  {option.IsClosed ? "Closed" : ""}
                </Box>
                {option.BucketChargedWeight && (
                  <span> ({option.BucketChargedWeight} lbs)</span>
                )}
              </>
            )}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            variant="outlined"
            fullWidth
            size="small"
            error={!!fieldError}
            helperText={fieldError?.message}
            sx={{
              color:
                isManualCharge && selectedValue?.IsClosed ? "red" : "inherit",
            }}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : (
                      params.InputProps.endAdornment
                    )}
                  </>
                ),
              },
            }}
          />
        )}
        value={selectedValue}
        onChange={(event, newValue) => {
          onChange(newValue ? newValue.BucketId : null);
          onBucketSelected(newValue);
        }}
        disabled={disabled}
      />
      <ErrorModal
        error={error}
        onHide={() => setError("")}
        title="Error loading buckets"
      />
    </>
  );
};
